import { AsyncPipe, NgOptimizedImage } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  Renderer2,
  ViewChild
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { AuthService } from '@portal/auth';
import { KENDO_POPUP } from '@progress/kendo-angular-popup';
import { IconComponent, IconSize } from '@ui/aos-lib';
import { asyncScheduler, type Observable, take } from 'rxjs';

import { selectUserSettingsForTemplate } from '../../../pages/help-and-settings/user-settings/store/user-settings.reducer';
import type { RouteItem } from '../../../routes/app.types';
import { ComponentMediaObserverService } from '../../../services/utility/component-media-observer.service';
import { Breakpoints } from '../../../services/utility/media-observer.types';
import { SideMenuCollapsedComponent } from './side-menu-collapsed/side-menu-collapsed.component';
import { SideMenuExpandedComponent } from './side-menu-expanded/side-menu-expanded.component';
import { SideMenuService } from './side-menu.service';
import { TranslateRegistry } from 'src/app/services/translate/translate.registry';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AsyncPipe,
    IconComponent,
    KENDO_POPUP,
    NgOptimizedImage,
    TranslateModule,
    SideMenuCollapsedComponent,
    SideMenuExpandedComponent
  ],
  providers: [ComponentMediaObserverService, SideMenuService],
  selector: 'aos-side-menu',
  styleUrls: ['./side-menu.component.scss', './side-menu-component-footer.scss'],
  templateUrl: './side-menu.component.html'
})
export class SideMenuComponent implements OnInit {
  readonly router = inject(Router);
  readonly route = inject(ActivatedRoute);
  readonly store = inject(Store);
  readonly authService = inject(AuthService);
  readonly mediaObserver = inject(ComponentMediaObserverService);
  readonly renderer = inject(Renderer2);
  readonly cdr = inject(ChangeDetectorRef);

  @Input() expanded = true;
  @Output() toggleClicked = new EventEmitter<void>();

  protected readonly hasBreakpointL$ = this.mediaObserver.hasBreakpoint(Breakpoints.L);

  protected readonly accountAvatarIconName = 'account_circle_filled';

  protected readonly aclaraConnectIconName = 'aclara-connect';
  protected readonly aclaraConnectText = 'Aclara Connect';

  protected readonly collapseButtonCollapsedIconSize = IconSize.MD;
  protected readonly collapseButtonExpandedIconSize = IconSize.LG;
  protected readonly collapseButtonIconColor = 'currentColor';
  protected readonly collapseButtonIconName = 'menu_collapse';

  protected readonly helpIconName = 'help-box-multiple';
  protected readonly helpText = TranslateRegistry.get.MAIN_MENU.TITLE.HELP;

  protected readonly logoutButtonIconName = 'log-out';
  protected readonly logoutText = TranslateRegistry.get.MAIN_MENU.TITLE.LOG_OUT;

  protected readonly sideMenuItemIconColor = 'currentColor';
  protected readonly sideMenuItemIconSize = IconSize.MD;

  protected readonly subMenuOpenerIconColor = 'currentColor';
  protected readonly subMenuOpenerIconName = 'dots-vertical';
  protected readonly subMenuOpenerIconSize = IconSize.MD;

  protected readonly userSettingsIconName = 'user-settings';
  protected readonly userSettingsText = TranslateRegistry.get.MAIN_MENU.TITLE.USER_SETTINGS;

  private readonly sideMenuService = inject(SideMenuService);
  protected readonly menuItems$: Observable<readonly RouteItem[]> = this.sideMenuService.filteredMenuItems$;

  protected readonly SideMenuCollapsedComponent = SideMenuCollapsedComponent;
  protected readonly SideMenuExpandedComponent = SideMenuExpandedComponent;

  protected readonly userSettings$ = this.store.select(selectUserSettingsForTemplate);

  protected showSettingsPopup = false;

  @ViewChild('userSettingsElement') userSettingsargetElementRef!: ElementRef;

  private readonly aclaraConnectLink = 'https://aclara.my.site.com/AclaraConnect/s/';
  private readonly helpLink = '/assets/help-page/';

  ngOnInit(): void {
    this.menuItems$.pipe(take(1)).subscribe(menuItems => {
      const selectedParentItem = menuItems.find(item => item.selected && item.children.length);
      selectedParentItem?.expandItem();
    });

    this.renderer.listen('document', 'click', () => {
      this.closePopups();
      this.cdr.detectChanges();
    });
  }

  protected closePopups(): void {
    this.showSettingsPopup = false;
  }

  protected navigateToHelp(): void {
    window.open(this.helpLink, '_blank');
  }

  protected navigateToAclaraConnect(): void {
    window.open(this.aclaraConnectLink, '_blank');
  }

  protected navigateToUserSettings(): void {
    this.router.navigate(['/user-settings']);
    this.showSettingsPopup = true;
  }

  protected toggleClickUserSettingsSubMenu(event?: MouseEvent): void {
    this.closePopups();

    if (event) {
      event.stopPropagation();
    }
    this.showSettingsPopup = !this.showSettingsPopup;
  }

  // Skip hover open/close for touch — the click handler (toggleClick*) already toggles these popups.
  protected enterUserSettingsSubMenu(evt?: PointerEvent): void {
    if (!this.expanded && evt?.pointerType !== 'touch') {
      this.showSettingsPopup = true;
    }
  }

  protected leaveUserSettingsSubMenu(evt?: PointerEvent): void {
    if (!this.expanded && evt?.pointerType !== 'touch') {
      asyncScheduler.schedule(() => (this.showSettingsPopup = false));
    }
  }

  protected logout(): void {
    this.showSettingsPopup = true;
    this.authService.logout();
  }
}
